import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Send, Mail, Users, Check, X, Loader2, ChevronDown,
  RefreshCw, AlertTriangle, Clock, FileText, Settings, Zap, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { LeadSequenceTab } from './LeadSequenceTab';
import { ClientUpdatesTab } from './ClientUpdatesTab';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api/superadmin';

const PLAN_LABELS = {
  essenziale: 'Essenziale', professional: 'Professional', elite: 'Elite', lifetime: 'Lifetime',
};

const EMAIL_TEMPLATES = [
  {
    id: 'promo_annuale',
    label: 'Promozione annuale',
    subject: 'Offerta speciale Shangrilux — sconto esclusivo per te',
    body: `<h2 style="font-family:Georgia,serif;font-weight:300;color:#fff;margin-top:0;">Ciao {{nome}},</h2>
<p style="color:#a0a0a0;line-height:1.8;">Grazie per aver esplorato Shangrilux. Abbiamo riservato un'<strong style="color:#fff">offerta esclusiva</strong> per strutture come la tua.</p>
<p style="color:#a0a0a0;line-height:1.8;">Per il piano <strong style="color:#c9a84c">{{piano}}</strong>, offriamo oggi:</p>
<ul style="color:#a0a0a0;line-height:2;">
  <li>✓ Onboarding assistito gratuito</li>
  <li>✓ Primo mese di supporto prioritario incluso</li>
  <li>✓ Setup smart home incluso (1 dispositivo)</li>
</ul>
<p style="color:#a0a0a0;line-height:1.8;">L'offerta è valida per 7 giorni. Rispondi a questa email per prenotare una chiamata con Francesco.</p>`,
  },
  {
    id: 'nuova_funzione',
    label: 'Aggiornamento funzionalità',
    subject: 'Novità Shangrilux — nuove funzioni disponibili',
    body: `<h2 style="font-family:Georgia,serif;font-weight:300;color:#fff;margin-top:0;">Ciao {{nome}},</h2>
<p style="color:#a0a0a0;line-height:1.8;">Abbiamo aggiornato Shangrilux con nuove funzionalità pensate per strutture luxury come <strong style="color:#fff">{{struttura}}</strong>.</p>
<p style="color:#a0a0a0;line-height:1.8;"><strong style="color:#c9a84c;">Cosa c'è di nuovo:</strong></p>
<ul style="color:#a0a0a0;line-height:2;">
  <li>✓ Guest App PWA con modalità offline migliorata</li>
  <li>✓ Nuovi brand smart home: Loxone e BTicino</li>
  <li>✓ Editor i18n — modifica i testi della piattaforma senza codice</li>
  <li>✓ Heatmap calendario tariffe</li>
</ul>
<p style="color:#a0a0a0;line-height:1.8;">Attiva la tua demo gratuita per testare tutto oggi stesso.</p>`,
  },
  {
    id: 'followup',
    label: 'Follow-up post demo',
    subject: 'Hai avuto modo di esplorare Shangrilux?',
    body: `<h2 style="font-family:Georgia,serif;font-weight:300;color:#fff;margin-top:0;">Ciao {{nome}},</h2>
<p style="color:#a0a0a0;line-height:1.8;">Hai recentemente richiesto una demo di Shangrilux per <strong style="color:#fff">{{struttura}}</strong>. Volevo assicurarmi che tu abbia avuto modo di esplorare tutte le funzionalità.</p>
<p style="color:#a0a0a0;line-height:1.8;">Ti è rimasta qualche domanda su:</p>
<ul style="color:#a0a0a0;line-height:2;">
  <li>→ Integrazione smart home con i tuoi dispositivi</li>
  <li>→ Il portale prenotazioni personalizzato</li>
  <li>→ Le chiavi virtuali per gli ospiti</li>
  <li>→ I prezzi e i piani disponibili</li>
</ul>
<p style="color:#a0a0a0;line-height:1.8;">Sono disponibile per una chiamata di 20 minuti. Rispondi a questa email o visita <a href="https://francescorusso.work" style="color:#c9a84c;">francescorusso.work</a>.</p>
<p style="color:#a0a0a0;">— Francesco Russo</p>`,
  },
];

function buildEmailHtml(bodyHtml, lead) {
  const body = bodyHtml
    .replace(/{{nome}}/g, lead?.name || 'Cliente')
    .replace(/{{struttura}}/g, lead?.company || 'la tua struttura')
    .replace(/{{piano}}/g, PLAN_LABELS[lead?.plan_interest] || 'Professional');

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"></head>
<body style="margin:0;padding:0;background:#080808;font-family:'DM Sans',Arial,sans-serif;color:#e0e0e0;">
<div style="max-width:600px;margin:40px auto;background:#121212;border:1px solid #222;">
  <div style="background:#0d0d0d;padding:24px 32px;border-bottom:1px solid #222;">
    <p style="margin:0;color:#c9a84c;font-size:11px;letter-spacing:4px;text-transform:uppercase;">Shangrilux Luxury Hospitality</p>
    <p style="margin:4px 0 0;color:#666;font-size:11px;">Smart Access & Booking · Luxury Hospitality</p>
  </div>
  <div style="padding:32px;">${body}</div>
  <div style="padding:16px 32px;border-top:1px solid #222;text-align:center;">
    <p style="color:#444;font-size:11px;margin:0;">
      Sviluppato da <a href="https://francescorusso.work" style="color:#666;">Francesco Russo</a> · 
      <a href="mailto:info@francescorusso.work" style="color:#666;">info@francescorusso.work</a>
    </p>
  </div>
</div>
</body></html>`;
}

export function MarketingTab({ storedKey, preselectedLead, onClearPreselect }) {
  const [leads, setLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [smtpStatus, setSmtpStatus] = useState(null); // null=loading, {configured:bool}
  // Sub-nav
  const [activeView, setActiveView] = useState('sequence'); // 'sequence' | 'client-updates' | 'compose' | 'history' | 'smtp'

  // Compose state
  const [recipientMode, setRecipientMode] = useState('all'); // 'all' | 'plan' | 'single'
  const [recipientPlan, setRecipientPlan] = useState('professional');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // SMTP config form
  const [smtpForm, setSmtpForm] = useState({ host: '', port: 587, user: '', pass: '', from_name: 'Shangrilux', from_email: '', secure: 'tls' });
  const [smtpSaving, setSmtpSaving] = useState(false);

  useEffect(() => {
    loadLeads();
    loadCampaigns();
    checkSmtp();
  }, []);

  useEffect(() => {
    if (preselectedLead) {
      setRecipientMode('single');
      setSelectedLeads([preselectedLead]);
      onClearPreselect?.();
      setActiveView('compose');
    }
  }, [preselectedLead]);

  const loadLeads = async () => {
    try {
      const { data } = await axios.get(`${API}/leads`, { headers: { 'X-Superadmin-Key': storedKey } });
      setLeads(data.leads || []);
    } catch {}
  };

  const loadCampaigns = async () => {
    try {
      const { data } = await axios.get(`${API}/marketing/campaigns`, { headers: { 'X-Superadmin-Key': storedKey } });
      setCampaigns(data.campaigns || []);
    } catch {}
  };

  const checkSmtp = async () => {
    try {
      const { data } = await axios.get(`${API}/smtp-config`, { headers: { 'X-Superadmin-Key': storedKey } });
      setSmtpStatus(data);
    } catch {
      setSmtpStatus({ configured: false });
    }
  };

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject);
    setBody(tpl.body);
    setActiveTemplate(tpl.id);
  };

  const getRecipientCount = () => {
    if (recipientMode === 'all') return leads.length;
    if (recipientMode === 'plan') return leads.filter(l => l.plan_interest === recipientPlan).length;
    return selectedLeads.length;
  };

  const sendCampaign = async () => {
    if (!subject.trim()) return toast.error('Inserisci l\'oggetto dell\'email.');
    if (!body.trim()) return toast.error('Inserisci il corpo dell\'email.');
    if (getRecipientCount() === 0) return toast.error('Nessun destinatario selezionato.');
    setSending(true);
    setSendResult(null);
    try {
      let recipients = [];
      if (recipientMode === 'all') recipients = leads.map(l => ({ email: l.email, name: l.name, company: l.company, plan_interest: l.plan_interest }));
      else if (recipientMode === 'plan') recipients = leads.filter(l => l.plan_interest === recipientPlan).map(l => ({ email: l.email, name: l.name, company: l.company, plan_interest: l.plan_interest }));
      else recipients = selectedLeads.map(l => ({ email: l.email, name: l.name, company: l.company, plan_interest: l.plan_interest }));

      const { data } = await axios.post(`${API}/marketing/send`, {
        subject, body_html: body, recipients,
      }, { headers: { 'X-Superadmin-Key': storedKey } });
      setSendResult(data);
      loadCampaigns();
      toast.success(`Campagna inviata: ${data.sent} email consegnate.`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Errore durante l\'invio.';
      toast.error(msg);
      setSendResult({ error: msg });
    } finally {
      setSending(false);
    }
  };

  const saveSmtp = async () => {
    setSmtpSaving(true);
    try {
      await axios.post(`${API}/smtp-config`, smtpForm, { headers: { 'X-Superadmin-Key': storedKey } });
      toast.success('Configurazione SMTP salvata.');
      checkSmtp();
      setActiveView('compose');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio SMTP.');
    } finally {
      setSmtpSaving(false);
    }
  };

  const setSmtpF = (k, v) => setSmtpForm(p => ({ ...p, [k]: v }));

  const toggleLeadSelection = (lead) => {
    setSelectedLeads(prev =>
      prev.some(l => l.email === lead.email)
        ? prev.filter(l => l.email !== lead.email)
        : [...prev, lead]
    );
  };

  return (
    <div data-testid="marketing-tab">
      {/* Sub-nav */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-1 p-1 rounded-sm" style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-1)' }}>
          {[
            { id: 'sequence', label: 'Sequenza Lead', icon: Zap },
            { id: 'client-updates', label: 'Aggiornamenti Clienti', icon: TrendingUp },
            { id: 'compose', label: 'Campagna Manuale', icon: Send },
            { id: 'history', label: 'Storico', icon: Clock },
            { id: 'smtp', label: 'Config SMTP', icon: Settings },
          ].map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setActiveView(id)}
              className="flex items-center gap-1.5 px-4 py-2 text-xs rounded-sm transition-all"
              style={{
                background: activeView === id ? 'var(--lux-gold)' : 'transparent',
                color: activeView === id ? '#000' : 'var(--lux-text-muted)',
              }}
              data-testid={`marketing-nav-${id}`}>
              <Icon size={12} /> {label}
            </button>
          ))}
        </div>
        {/* SMTP indicator */}
        <div className="flex items-center gap-1.5 text-xs" style={{ color: smtpStatus?.configured ? '#059669' : '#dc2626' }}>
          <div className="w-2 h-2 rounded-full" style={{ background: smtpStatus?.configured ? '#059669' : '#dc2626' }} />
          {smtpStatus?.configured ? `SMTP: ${smtpStatus.host}` : 'SMTP non configurato'}
        </div>
      </div>

      {/* LEAD SEQUENCE TAB */}
      {activeView === 'sequence' && (
        <LeadSequenceTab storedKey={storedKey} />
      )}

      {/* CLIENT UPDATES TAB */}
      {activeView === 'client-updates' && (
        <ClientUpdatesTab storedKey={storedKey} />
      )}

      {/* SMTP warning banner */}
      {!smtpStatus?.configured && activeView === 'compose' && (
        <div className="flex items-center gap-3 p-4 rounded-sm mb-6"
          style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)' }}>
          <AlertTriangle size={16} style={{ color: '#dc2626', flexShrink: 0 }} />
          <div className="flex-1">
            <p className="text-sm font-medium" style={{ color: '#dc2626' }}>SMTP non configurato</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--lux-text-muted)' }}>
              Per inviare campagne email è necessario configurare un server SMTP.
            </p>
          </div>
          <button onClick={() => setActiveView('smtp')}
            className="text-xs px-3 py-1.5 rounded-sm shrink-0"
            style={{ background: 'rgba(220,38,38,0.15)', color: '#dc2626', border: '1px solid rgba(220,38,38,0.3)' }}>
            Configura ora
          </button>
        </div>
      )}

      {/* COMPOSE VIEW */}
      {activeView === 'compose' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: settings */}
          <div className="space-y-5">
            {/* Recipients */}
            <div className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
              <p className="text-xs uppercase tracking-widest mb-4" style={{ color: 'var(--lux-gold)' }}>Destinatari</p>
              <div className="space-y-2">
                {[
                  { id: 'all', label: `Tutti i lead (${leads.length})` },
                  { id: 'plan', label: 'Per piano di interesse' },
                  { id: 'single', label: `Selezionati (${selectedLeads.length})` },
                ].map(({ id, label }) => (
                  <label key={id} className="flex items-center gap-2 cursor-pointer">
                    <div className="w-4 h-4 rounded-full border flex items-center justify-center shrink-0"
                      style={{ borderColor: recipientMode === id ? 'var(--lux-gold)' : 'var(--lux-border-2)', background: recipientMode === id ? 'var(--lux-gold)' : 'transparent' }}
                      onClick={() => setRecipientMode(id)}>
                      {recipientMode === id && <div className="w-1.5 h-1.5 rounded-full bg-black" />}
                    </div>
                    <span className="text-sm" style={{ color: 'var(--lux-text-sec)' }}>{label}</span>
                  </label>
                ))}
              </div>
              {recipientMode === 'plan' && (
                <select value={recipientPlan} onChange={e => setRecipientPlan(e.target.value)}
                  className="w-full mt-3 px-3 py-2.5 text-sm rounded-sm"
                  style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }}>
                  {Object.entries(PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              )}
              {recipientMode === 'single' && (
                <div className="mt-3 space-y-1 max-h-40 overflow-y-auto">
                  {leads.map((l, i) => (
                    <label key={i} className="flex items-center gap-2 cursor-pointer py-1">
                      <div className="w-4 h-4 rounded border flex items-center justify-center shrink-0"
                        style={{ borderColor: selectedLeads.some(s => s.email === l.email) ? 'var(--lux-gold)' : 'var(--lux-border-2)', background: selectedLeads.some(s => s.email === l.email) ? 'var(--lux-gold)' : 'transparent' }}
                        onClick={() => toggleLeadSelection(l)}>
                        {selectedLeads.some(s => s.email === l.email) && <Check size={10} color="#000" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate" style={{ color: 'var(--lux-text)' }}>{l.name}</p>
                        <p className="text-[10px] truncate" style={{ color: 'var(--lux-text-muted-3)' }}>{l.email}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Templates */}
            <div className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
              <p className="text-xs uppercase tracking-widest mb-3" style={{ color: 'var(--lux-gold)' }}>Template pronti</p>
              <div className="space-y-2">
                {EMAIL_TEMPLATES.map(tpl => (
                  <button key={tpl.id} onClick={() => applyTemplate(tpl)}
                    className="w-full text-left px-3 py-2.5 text-xs rounded-sm transition-all"
                    style={{
                      background: activeTemplate === tpl.id ? 'rgba(201,168,76,0.1)' : 'var(--lux-surface-3)',
                      border: `1px solid ${activeTemplate === tpl.id ? 'rgba(201,168,76,0.4)' : 'var(--lux-border-2)'}`,
                      color: activeTemplate === tpl.id ? 'var(--lux-gold)' : 'var(--lux-text-muted)',
                    }}
                    data-testid={`template-btn-${tpl.id}`}>
                    <div className="flex items-center gap-2">
                      <FileText size={11} />
                      {tpl.label}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right: compose */}
          <div className="lg:col-span-2 space-y-4">
            <div className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
              <p className="text-xs uppercase tracking-widest mb-4" style={{ color: 'var(--lux-gold)' }}>Composizione email</p>

              {/* Subject */}
              <div className="mb-4">
                <label className="block text-[10px] uppercase tracking-widest mb-1.5" style={{ color: 'var(--lux-text-muted-2)' }}>Oggetto</label>
                <input value={subject} onChange={e => setSubject(e.target.value)}
                  placeholder="es. Offerta speciale Shangrilux per te..."
                  className="w-full px-4 py-3 text-sm rounded-sm"
                  style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }}
                  data-testid="campaign-subject-input" />
              </div>

              {/* Body */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-[10px] uppercase tracking-widest" style={{ color: 'var(--lux-text-muted-2)' }}>Corpo email (HTML)</label>
                  <span className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>
                    Variabili: {'{{nome}}'} {'{{struttura}}'} {'{{piano}}'}
                  </span>
                </div>
                <textarea value={body} onChange={e => setBody(e.target.value)}
                  rows={14}
                  placeholder="Scrivi il corpo della tua email in HTML, oppure scegli un template a sinistra..."
                  className="w-full px-4 py-3 text-sm rounded-sm font-mono resize-y"
                  style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)', minHeight: '280px' }}
                  data-testid="campaign-body-input" />
              </div>

              {/* Send button */}
              <div className="flex items-center gap-4">
                <button onClick={sendCampaign} disabled={sending || !smtpStatus?.configured}
                  className="flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: 'var(--lux-gold)', color: '#000' }}
                  data-testid="send-campaign-btn">
                  {sending ? <><Loader2 size={14} className="animate-spin" /> Invio in corso...</> : <><Send size={14} /> Invia a {getRecipientCount()} lead</>}
                </button>
                <p className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>
                  {!smtpStatus?.configured ? 'Configura SMTP per abilitare l\'invio' : `${getRecipientCount()} destinatari selezionati`}
                </p>
              </div>

              {/* Result */}
              {sendResult && !sendResult.error && (
                <div className="mt-4 p-4 rounded-sm" style={{ background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.2)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Check size={16} style={{ color: '#059669' }} />
                    <p className="text-sm font-medium" style={{ color: '#059669' }}>Campagna inviata con successo</p>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>
                    {sendResult.sent} email consegnate · {sendResult.failed} errori
                  </p>
                </div>
              )}
              {sendResult?.error && (
                <div className="mt-4 p-4 rounded-sm" style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)' }}>
                  <p className="text-sm" style={{ color: '#dc2626' }}>{sendResult.error}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* HISTORY VIEW */}
      {activeView === 'history' && (
        <div>
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-serif text-xl font-light" style={{ color: 'var(--lux-text)' }}>Storico campagne</h3>
            <button onClick={loadCampaigns} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm"
              style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
              <RefreshCw size={12} /> Aggiorna
            </button>
          </div>
          {campaigns.length === 0 ? (
            <div className="text-center py-16" style={{ color: 'var(--lux-text-muted)' }}>
              <Mail size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Nessuna campagna inviata ancora.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {campaigns.map((c, i) => (
                <div key={i} className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-sm" style={{ color: 'var(--lux-text)' }}>{c.subject}</p>
                      <p className="text-xs mt-1" style={{ color: 'var(--lux-text-muted)' }}>
                        {new Date(c.sent_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium" style={{ color: '#059669' }}>{c.sent} inviate</p>
                      {c.failed > 0 && <p className="text-xs" style={{ color: '#dc2626' }}>{c.failed} errori</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SMTP CONFIG VIEW */}
      {activeView === 'smtp' && (
        <div className="max-w-xl">
          <div className="p-6 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
            <p className="text-xs uppercase tracking-widest mb-5" style={{ color: 'var(--lux-gold)' }}>
              Configurazione SMTP Master
            </p>
            <p className="text-xs mb-6 leading-relaxed" style={{ color: 'var(--lux-text-muted)' }}>
              Questi dati vengono usati per inviare newsletter e promozioni ai lead. Sono separati dalla configurazione SMTP dei singoli tenant.
            </p>
            <div className="space-y-4">
              <SmtpField label="Server SMTP (host)" value={smtpForm.host} onChange={v => setSmtpF('host', v)} placeholder="smtp.gmail.com" testId="smtp-host" />
              <div className="grid grid-cols-2 gap-3">
                <SmtpField label="Porta" type="number" value={smtpForm.port} onChange={v => setSmtpF('port', Number(v))} placeholder="587" testId="smtp-port" />
                <div>
                  <label className="block text-[10px] uppercase tracking-widest mb-1.5" style={{ color: 'var(--lux-text-muted-2)' }}>Sicurezza</label>
                  <select value={smtpForm.secure} onChange={e => setSmtpF('secure', e.target.value)}
                    className="w-full px-4 py-3 text-sm rounded-sm"
                    style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }}>
                    <option value="tls">STARTTLS (porta 587)</option>
                    <option value="ssl">SSL (porta 465)</option>
                    <option value="none">Nessuna</option>
                  </select>
                </div>
              </div>
              <SmtpField label="Username / Email" value={smtpForm.user} onChange={v => setSmtpF('user', v)} placeholder="info@francescorusso.work" testId="smtp-user" />
              <SmtpField label="Password" type="password" value={smtpForm.pass} onChange={v => setSmtpF('pass', v)} placeholder="••••••••••••" testId="smtp-pass" />
              <SmtpField label="Nome mittente" value={smtpForm.from_name} onChange={v => setSmtpF('from_name', v)} placeholder="Shangrilux" testId="smtp-from-name" />
              <SmtpField label="Email mittente" type="email" value={smtpForm.from_email} onChange={v => setSmtpF('from_email', v)} placeholder="info@francescorusso.work" testId="smtp-from-email" />
            </div>
            <button onClick={saveSmtp} disabled={smtpSaving}
              className="mt-6 w-full py-3 text-sm font-medium flex items-center justify-center gap-2 rounded-sm"
              style={{ background: 'var(--lux-gold)', color: '#000' }}
              data-testid="save-smtp-btn">
              {smtpSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Salva configurazione SMTP
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SmtpField({ label, value, onChange, type = 'text', placeholder, testId }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest mb-1.5" style={{ color: 'var(--lux-text-muted-2)' }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-4 py-3 text-sm rounded-sm"
        style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }}
        data-testid={testId} />
    </div>
  );
}
